import { useEffect, useRef, useState } from 'react';
import { pollLeaderIds, pollShare } from '@components/polls/pollFormat';
import type { LiveQuestion, LiveState } from '@models/api';
import { linkDisplay, liveLink, placePageLink } from '@lib/livePolls/liveLinks';
import { RESULTS_PAGE_SIZE, shownResultsPage } from '@lib/livePolls/resultsPaging';
import { serverNow } from '@lib/livePolls/serverClock';
import { useFitLevel } from '@lib/livePolls/useFitLevel';
import { cn } from '@lib/utils';
import { LIVE_POLL_COPY } from '../livePollCopy';
import { DAWN, type LiveScreenColor } from '@lib/livePolls/liveScreenLook';
import { BigScreenQr, PlaceLogo, ReconnectingChip, ShareBar } from './BigScreenParts';
import { useStageBox } from './BigScreenStage';

/** Up to 3 questions: B3's single column; 4 to 6: B3b's two columns of 3, no paging; more: pages of 6. */
const SINGLE_COLUMN_MAX = 3;

const LEVELS = [
  {
    root: 'gap-[34px] pt-14 pb-12',
    group: 'gap-[22px]',
    clamp: 'line-clamp-2',
    h: 'text-[48px] leading-[54px]',
  },
  {
    root: 'gap-5 pt-10 pb-8',
    group: 'gap-4',
    clamp: 'line-clamp-2',
    h: 'text-[44px] leading-[50px]',
  },
  {
    root: 'gap-4 pt-8 pb-6',
    group: 'gap-3',
    clamp: 'line-clamp-1',
    h: 'text-[40px] leading-[46px]',
  },
] as const;

/** A question's most bars: more answers tied than this are counted on its small line (founder, 3 Nov). */
const MAX_TOP_BARS = 2;

/** The top answers, every tied one, by position; none when nobody answered. */
const topOptions = (question: LiveQuestion) => {
  const leaders = pollLeaderIds(question);
  return question.options
    .filter(option => leaders.has(option.id))
    .sort((a, b) => a.position - b.position);
};

/**
 * A 1 s clock while the results rotate, for the page and its countdown, on the server's clock (`serverNow`):
 * the page comes from the server-held `page_since`, so a projector clock that is off must not move it.
 */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => serverNow());
  useEffect(() => {
    if (!active) return;
    // Catch up at once when rotation (re)starts (a resume after a pause), then every second.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing with the wall clock, an external system
    setNow(serverNow());
    const timer = setInterval(() => {
      setNow(serverNow());
    }, 1_000);
    return () => {
      clearInterval(timer);
    };
  }, [active]);
  return now;
}

interface JoinPanel {
  name: string;
  imageUrl: string | null;
  tagline: string | null;
  /** What the QR opens and the room types. */
  link: string;
  /** The place's own page, whose address can be too long for one line of the panel. */
  isPage: boolean;
}

/**
 * The B3 join panel follows the poll's place (founder, 2026-11-03). A public Event: the Event and its page, where
 * people tap Going themselves, with or without a host Space. A Space: hidden when nobody could join it (R19; the
 * 10:20 note's `none`), else the Space and its page. No public address (an unlisted Space, a private Event): the
 * join Space and the live link, which is the only way in that puts no invite token on the projector. Neither: no panel.
 */
const joinPanelFor = (state: LiveState): JoinPanel | null => {
  const { place, join_space: space, live_code: code } = state;
  const page = placePageLink(place);
  if (place.type === 'event' && page !== null) {
    return {
      name: place.name,
      imageUrl: place.image_url,
      tagline: null,
      link: page,
      isPage: true,
    };
  }
  if (!space || space.join_type === 'none') return null;
  return {
    name: space.name,
    imageUrl: space.image_url,
    tagline: space.tagline ?? null,
    link: page ?? liveLink(code),
    isPage: page !== null,
  };
};

function ResultItem({
  question,
  compact,
  clamp,
}: {
  question: LiveQuestion;
  compact: boolean;
  clamp: string;
}) {
  const tops = topOptions(question);
  return (
    <div
      className={cn('flex min-w-0 flex-col [&>*]:shrink-0', compact ? 'gap-1.5' : 'gap-2')}
      data-testid="big-screen-result"
    >
      <p
        className={cn(
          'break-words font-fira font-semibold text-mb-ink',
          clamp,
          compact ? 'text-[22px] leading-7' : 'text-[24px] leading-[30px]',
        )}
      >
        {LIVE_POLL_COPY.numberedQuestion(question.position + 1, question.text)}
      </p>
      {tops.slice(0, MAX_TOP_BARS).map(top => (
        <ShareBar
          key={top.id}
          label={top.text}
          share={pollShare(top.votes_count, question.voters_count)}
          className={cn(
            'gap-3 rounded-[10px] py-1',
            compact ? 'min-h-[52px] px-3' : 'min-h-14 px-4',
          )}
          labelClassName={cn(
            clamp,
            compact ? 'text-[26px] leading-[30px]' : 'text-[28px] leading-8',
          )}
          shareClassName={compact ? 'text-[26px] leading-[30px]' : 'text-[28px] leading-8'}
        />
      ))}
      <p
        className={cn(
          'font-fira font-normal',
          compact
            ? 'text-[20px] leading-6 text-mb-dark-gray'
            : 'text-[20px] leading-[26px] text-mb-gray-dark',
        )}
      >
        {tops.length > MAX_TOP_BARS
          ? LIVE_POLL_COPY.answersAndTied(question.voters_count, tops.length)
          : LIVE_POLL_COPY.answers(question.voters_count)}
      </p>
    </div>
  );
}

export interface BigScreenResultsProps {
  state: LiveState;
  reconnecting?: boolean;
  /** The host's colour for the join panel; Dawn, the frames' backdrop, without one. */
  color?: LiveScreenColor;
}

/**
 * B3: "Here's what the room said", per question its top answer with its share and "{n} answers" (no
 * runner-ups, L20; up to 2 bars for a tie, the small line counting any more), and the join panel (`joinPanelFor`). B3b: more than 6 questions → pages of 6 in two columns
 * of 3, the page computed from the server-held `results_paging` (PR2), "Page {p} of {q} · next page in {s} s".
 * The same on every screen: the author's
 * paging controls (B3c) are `BigScreenPagingBar`, drawn outside the stage.
 */
export function BigScreenResults({
  state,
  reconnecting = false,
  color = DAWN,
}: BigScreenResultsProps) {
  const results = [...(state.results ?? [])].sort((a, b) => a.position - b.position);
  // Ended before any question was shown (F4): no list to show, and no "0 questions" eyebrow either.
  const noneRun = results.length === 0;
  const paged = results.length > RESULTS_PAGE_SIZE;
  const now = useNow(paged && !state.results_paging?.paused);
  const shown = shownResultsPage(state.results_paging, results.length, now, state.closed_at);
  const pageItems = paged
    ? results.slice(shown.page * RESULTS_PAGE_SIZE, (shown.page + 1) * RESULTS_PAGE_SIZE)
    : results;
  const twoColumns = pageItems.length > SINGLE_COLUMN_MAX || paged;
  const columns = twoColumns ? [pageItems.slice(0, 3), pageItems.slice(3)] : [pageItems];

  const join = joinPanelFor(state);

  const rootRef = useRef<HTMLDivElement>(null);
  const stage = useStageBox();
  const fitKey = `${shown.page}|${pageItems.map(q => `${q.position}:${q.text}:${topOptions(q).length}`).join('\u0000')}|${join?.name ?? ''}|${Math.round(stage.width)}x${Math.round(stage.height)}`;
  const level = useFitLevel(rootRef, LEVELS.length - 1, fitKey);
  const l = LEVELS[level];

  return (
    <div
      className="relative flex h-full w-full overflow-hidden bg-mb-bg"
      data-testid="big-screen-results"
    >
      <div
        ref={rootRef}
        className={cn(
          'relative flex min-w-0 flex-1 flex-col justify-center overflow-hidden pl-[88px] pr-16 [&>*]:shrink-0',
          l.root,
        )}
        data-fit-level={level}
      >
        {reconnecting ? (
          <div className="absolute left-[88px] top-3">
            <ReconnectingChip />
          </div>
        ) : null}
        <div className="flex flex-col gap-1.5">
          {noneRun ? null : (
            <p className="font-fira text-[20px] font-semibold uppercase leading-6 tracking-[0.08em] text-mb-gray-dark">
              {LIVE_POLL_COPY.resultsEyebrow(results.length)}
            </p>
          )}
          <h1 className={cn('font-livvic font-bold tracking-[-0.015em] text-mb-ink', l.h)}>
            {noneRun ? LIVE_POLL_COPY.removedTitle : LIVE_POLL_COPY.resultsHeading}
          </h1>
        </div>

        {noneRun ? null : (
          <div className={cn('flex', twoColumns ? 'max-w-[648px] gap-6' : 'max-w-[648px]')}>
            {columns.map((column, i) => (
              <div
                key={i}
                className={cn('flex min-w-0 flex-1 basis-0 flex-col [&>*]:shrink-0', l.group)}
              >
                {column.map(question => (
                  <ResultItem
                    key={question.position}
                    question={question}
                    compact={twoColumns}
                    clamp={l.clamp}
                  />
                ))}
              </div>
            ))}
          </div>
        )}

        {paged ? (
          <div className="pt-2">
            <div className="flex items-center gap-1.5" data-testid="big-screen-paging">
              {Array.from({ length: shown.pages }, (_, i) => (
                <span
                  key={i}
                  aria-hidden="true"
                  className={cn(
                    'h-1.5 rounded-full',
                    i === shown.page ? 'w-5 bg-mb-teal-ink' : 'w-1.5 bg-mb-gray-muted',
                  )}
                />
              ))}
              <span className="pl-1 font-fira text-[20px] font-normal leading-6 text-mb-dark-gray">
                {shown.paused || shown.secondsLeft === null
                  ? LIVE_POLL_COPY.pagePaused(shown.page + 1, shown.pages)
                  : LIVE_POLL_COPY.pageOf(shown.page + 1, shown.pages, shown.secondsLeft)}
              </span>
            </div>
          </div>
        ) : null}
      </div>

      {join ? (
        <div
          className="flex w-[480px] shrink-0 flex-col justify-center gap-[22px] overflow-hidden px-14 py-12 [&>*]:shrink-0"
          style={{
            background: color.background,
            color: color.textColor,
          }}
          data-testid="big-screen-join-panel"
        >
          <div className="flex items-center gap-4">
            <PlaceLogo
              name={join.name}
              imageUrl={join.imageUrl}
              className="h-[88px] w-[88px] rounded-[20px]"
              initialsClassName="text-[36px] leading-[44px]"
            />
            <div className="flex min-w-0 flex-col gap-1">
              <p className="font-livvic text-[44px] font-bold leading-[48px] tracking-[-0.015em]">
                {LIVE_POLL_COPY.joinLabel}
              </p>
              {join.tagline ? (
                <p className="line-clamp-2 font-fira text-[20px] font-normal leading-[26px]">
                  {join.tagline}
                </p>
              ) : null}
            </div>
          </div>
          <h2 className="line-clamp-3 break-words font-livvic text-[44px] font-bold leading-[48px] tracking-[-0.015em]">
            {join.name}
          </h2>
          <p className="font-livvic text-[22px] font-semibold leading-[30px]">
            {LIVE_POLL_COPY.peopleMayBeHere}
          </p>
          <div className="flex h-[240px] w-[240px] items-center justify-center rounded-[20px] bg-white">
            <BigScreenQr link={join.link} size={224} />
          </div>
          <p className="font-fira text-[20px] font-semibold leading-[26px]">
            {LIVE_POLL_COPY.scanToJoin}{' '}
            <span className={join.isPage ? '[overflow-wrap:anywhere]' : 'whitespace-nowrap'}>
              {linkDisplay(join.link)}
            </span>
          </p>
        </div>
      ) : null}
    </div>
  );
}
